"use client";

import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { DataField, Stack } from "@leabankab/component-library";
import { SummarySection } from "@/components/ui";
import { useCurrency } from "@/hooks";
import { formatLoanType } from "@/shared/formaters";
import { useConstantOptions } from "../../customer-information/hooks/use-constant-options";
import { LoanSummaryPageContext } from "../types";
import { AboutTheLoan } from "./lists/about-the-loan-list";
import { InsuranceList } from "./lists/insurance-list";
import { PersonalInfo } from "./lists/personal-info-list";

interface Props {
	context: LoanSummaryPageContext;
	hideDebts?: boolean;
}

const LoanSummaryComponent = ({ context, hideDebts }: Props) => {
	const t = useTranslations("pages.SummaryPage");
	const { payouts } = context;
	const { format } = useCurrency();

	const loanTypeOptions = useConstantOptions("loan-types");

	const refinanceDebtsArray = payouts?.map((payout) => ({
		type: formatLoanType(payout.description, loanTypeOptions),
		amount: payout.amount,
		bank: payout.bank,
	}));

	const sumDebts = useMemo(
		() =>
			refinanceDebtsArray
				? refinanceDebtsArray
						.map((debt) => debt.amount)
						.reduce((acc, amount) => acc + amount, 0)
				: 0,
		[refinanceDebtsArray]
	);

	return (
		<Stack className="gap-1">
			<SummarySection title={t("loan.title")} defaultExpanded>
				<AboutTheLoan context={context} hideDebts={hideDebts} />
			</SummarySection>
			{refinanceDebtsArray && refinanceDebtsArray.length > 0 && (
				<SummarySection title={t("refinance.title")}>
					<Stack>
						{refinanceDebtsArray.map((debt, index) => (
							<DataField
								key={debt.type + index}
								label={debt.bank + " - " + debt.type}
								value={format(debt.amount)}
							/>
						))}
						<DataField
							label={t("refinance.minimisedSumLabel")}
							value={format(sumDebts)}
							divider={false}
						/>
					</Stack>
				</SummarySection>
			)}
			<SummarySection title={t("personalInfo.title")}>
				<PersonalInfo context={context} />
			</SummarySection>
			<InsuranceList context={context} />
		</Stack>
	);
};

export default LoanSummaryComponent;
